import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Delete, LogIn, LogOut } from "lucide-react";
import { api } from "@/lib/api";

export default function Clock() {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    api.get("/clock/employees").then((r) => setEmployees(r.data)).catch(() => {});
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (pin.length === 4 && selected) {
      submit(pin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const submit = async (code) => {
    try {
      const { data } = await api.post("/clock", { employee_id: selected.id, pin: code });
      setResult(data);
      setPin("");
      setSelected(null);
      setTimeout(() => setResult(null), 3500);
    } catch {
      setError("Wrong PIN — try again");
      setPin("");
      setTimeout(() => setError(""), 2000);
    }
  };

  const timeStr = now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" });
  const dateStr = now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });

  return (
    <div data-testid="clock-page" className="flex min-h-screen flex-col items-center bg-deep px-4 py-10 font-body text-cream">
      <img src="/logo.png" alt="" className="h-16 w-16 rounded-full shadow-lg" />
      <h1 className="mt-4 font-display text-2xl font-bold sm:text-3xl">Team Time Clock</h1>
      <p data-testid="clock-time" className="mt-2 font-editorial text-xl italic text-gold">{timeStr}</p>
      <p className="text-sm text-cream/50">{dateStr}</p>

      {employees.length === 0 ? (
        <p data-testid="clock-empty" className="mt-12 max-w-sm text-center text-sm text-cream/60">
          No employees yet — the owner can add the team from the dashboard at /admin.
        </p>
      ) : (
        <div className="mt-8 w-full max-w-md">
          <p className="mb-3 text-center text-xs font-bold uppercase tracking-[0.25em] text-gold">
            1 · Who's clocking?
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {employees.map((e) => (
              <button
                key={e.id}
                data-testid={`clock-employee-${e.id}`}
                onClick={() => {
                  setSelected(e);
                  setPin("");
                }}
                className={`rounded-2xl border-2 px-4 py-3.5 font-display text-base font-semibold transition-all active:scale-95 ${
                  selected?.id === e.id
                    ? "border-gold bg-gold text-chalk shadow-lg shadow-gold/25"
                    : "border-cream/15 bg-cream/5 text-cream hover:border-gold/50"
                }`}
              >
                {e.name}
              </button>
            ))}
          </div>

          <p className="mb-3 mt-8 text-center text-xs font-bold uppercase tracking-[0.25em] text-gold">
            2 · Enter your PIN
          </p>
          <div className="mb-5 flex justify-center gap-3" data-testid="clock-pin-display">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className={`h-4 w-4 rounded-full transition-colors ${
                  pin.length > i ? "bg-gold" : "bg-cream/15"
                }`}
              />
            ))}
          </div>
          {error && (
            <p data-testid="clock-error" className="mb-4 text-center text-sm font-bold text-red-300">
              {error}
            </p>
          )}
          <div className="mx-auto grid max-w-xs grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
              <button
                key={d}
                data-testid={`clock-digit-${d}`}
                disabled={!selected}
                onClick={() => setPin((p) => (p.length < 4 ? p + d : p))}
                className="rounded-2xl bg-cream/8 py-4 font-display text-2xl font-semibold text-cream transition-all hover:bg-cream/15 active:scale-95 disabled:opacity-30"
              >
                {d}
              </button>
            ))}
            <span />
            <button
              data-testid="clock-digit-0"
              disabled={!selected}
              onClick={() => setPin((p) => (p.length < 4 ? p + "0" : p))}
              className="rounded-2xl bg-cream/8 py-4 font-display text-2xl font-semibold text-cream transition-all hover:bg-cream/15 active:scale-95 disabled:opacity-30"
            >
              0
            </button>
            <button
              data-testid="clock-delete-button"
              onClick={() => setPin((p) => p.slice(0, -1))}
              className="flex items-center justify-center rounded-2xl bg-cream/8 py-4 text-cream transition-all hover:bg-cream/15 active:scale-95"
              aria-label="Delete digit"
            >
              <Delete className="h-6 w-6" />
            </button>
          </div>
          {!selected && (
            <p className="mt-5 text-center text-xs text-cream/40">Tap your name first, then your 4-digit PIN.</p>
          )}
        </div>
      )}

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-deep/90 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.8, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              data-testid="clock-result"
              className="rounded-3xl bg-cream p-10 text-center text-chalk shadow-2xl"
            >
              {result.action === "in" ? (
                <LogIn className="mx-auto h-14 w-14 text-artichoke" />
              ) : (
                <LogOut className="mx-auto h-14 w-14 text-grape" />
              )}
              <h2 className="mt-4 font-display text-3xl font-bold">
                {result.action === "in" ? "Clocked in!" : "Clocked out!"}
              </h2>
              <p className="mt-2 text-lg font-semibold text-chalk/70">{result.employee_name}</p>
              <p className="mt-1 font-editorial text-xl italic text-crispy">
                {new Date(result.time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
